
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CaseStudyCard from "@/components/CaseStudyCard";
import PageTransition from "@/components/PageTransition";
import { fetchCaseStudies, type CaseStudy } from "@/services/caseStudyService";

const CaseStudiesPage = () => {
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    const loadCaseStudies = async () => {
      try {
        setLoading(true);
        const data = await fetchCaseStudies();
        setCaseStudies(data);
      } catch (error) {
        console.error("Failed to load case studies:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCaseStudies();
  }, []);

  // Get unique categories from case studies
  const categories = ["all", ...new Set(caseStudies.map(study => study.category.toLowerCase()))];

  // Filter case studies based on active filter
  const filteredCaseStudies = activeFilter === "all" 
    ? caseStudies 
    : caseStudies.filter(study => study.category.toLowerCase() === activeFilter);

  return (
    <PageTransition>
      <Helmet>
        <title>Case Studies | DesertSEO - Los Angeles SEO Results</title>
        <meta name="description" content="Browse our case studies to see real results we've achieved for our Los Angeles clients. Discover how our SEO strategies can work for your business." />
      </Helmet>

      <Navbar />

      <section className="py-20 bg-gradient-to-b from-muted/50 to-background">
        <div className="page-container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-desert-sky/10 text-desert-sky">
              Success Stories
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="text-gradient bg-sky-gradient">Case Studies</span>
            </h1>
            <p className="text-lg text-foreground/70">
              Browse through our success stories to see how we've helped businesses in Los Angeles achieve remarkable growth through strategic SEO solutions.
            </p>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setActiveFilter(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeFilter === category
                    ? "bg-primary text-white"
                    : "bg-muted hover:bg-muted/80"
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>

          {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-96 rounded-xl bg-muted animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCaseStudies.map((caseStudy, index) => (
              <motion.div
                key={caseStudy.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={caseStudy.featured ? "md:col-span-2" : ""}
              >
                <CaseStudyCard {...caseStudy} />
              </motion.div>
            ))}
          </div>
        )}

          {filteredCaseStudies.length === 0 && !loading && (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold mb-2">No case studies found</h3>
              <p className="text-muted-foreground">
                There are currently no case studies in this category. Please check back later or select another category.
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </PageTransition>
  );
};

export default CaseStudiesPage;
